import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { departmentsApi, positionsApi, staffSchedules } from '@/api/hr';
import { orgs } from '@/api/catalogs';

const fmt2 = (v: string | number | null) =>
  v == null ? '—' : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Друкована форма штатного розкладу на дату (зріз з проведених документів).
 * Frontend-друк (патерн StaffTransferPrintPage). Параметри — у query:
 * ?organization_id=...&on_date=YYYY-MM-DD.
 */
export default function StaffSchedulePrintPage() {
  const [sp] = useSearchParams();
  const orgId = sp.get('organization_id') ?? '';
  const onDate = sp.get('on_date') ?? '';
  const groupBy = (sp.get('group_by') === 'position' ? 'position' : 'employee') as 'employee' | 'position';
  const byEmp = groupBy === 'employee';

  const snapQ = useQuery({
    queryKey: ['staff-schedule-snapshot-print', orgId, onDate, groupBy],
    queryFn: () => staffSchedules.snapshot(orgId, onDate, groupBy),
    enabled: !!orgId && !!onDate,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const depQ = useQuery({
    queryKey: ['departments', orgId],
    queryFn: () => departmentsApi.list({ organization_id: orgId, limit: 1000 }),
    enabled: !!orgId,
  });
  const posQ = useQuery({ queryKey: ['positions-all'], queryFn: () => positionsApi.list({ limit: 1000 }) });

  const depName = useMemo(() => {
    const m = new Map<string, string>();
    depQ.data?.items.forEach((d) => m.set(d.id, d.name));
    return m;
  }, [depQ.data]);
  const posName = useMemo(() => {
    const m = new Map<string, string>();
    posQ.data?.items.forEach((p) => m.set(p.id, p.name));
    return m;
  }, [posQ.data]);

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const rows = useMemo(() => (snapQ.data ?? []).map((r) => ({
    pib: r.employee_name ?? '',
    dept: r.department_id ? (depName.get(r.department_id) ?? '—') : '—',
    pos: r.position_id ? (posName.get(r.position_id) ?? '—') : '—',
    rate: r.rate_count, salary: r.salary,
    // Місячний ФОТ: monthly_fot (HOURLY = ставка×норма_годин; backend уже ×к-сть людей
    // для position). Fallback на salary для старих відповідей без поля.
    fund: Number(r.monthly_fot ?? r.salary),
  })).sort((a, b) => (byEmp ? a.pib.localeCompare(b.pib) : a.dept.localeCompare(b.dept) || a.pos.localeCompare(b.pos))),
  [snapQ.data, depName, posName, byEmp]);

  if (snapQ.isLoading) return <div style={{ padding: '2rem' }}>Завантаження…</div>;

  const org = orgsQ.data?.items.find((o) => o.id === orgId);
  const totalRate = rows.reduce((s, r) => s + Number(r.rate), 0);
  const totalFund = rows.reduce((s, r) => s + r.fund, 0);

  return (
    <div className="print-page" style={{
      padding: '15mm', fontFamily: 'Times, serif', fontSize: '10pt',
      maxWidth: '210mm', margin: '0 auto', background: 'white', lineHeight: 1.4,
    }}>
      <div className="print-hide" style={{
        position: 'sticky', top: 0, background: '#f1f5f9',
        padding: '0.5rem 1rem', marginBottom: '1rem', borderRadius: 6,
        display: 'flex', gap: '0.5rem', alignItems: 'center',
      }}>
        <strong>Штатний розклад</strong>
        <button className="primary" onClick={() => window.print()} style={{ marginLeft: 'auto' }}>
          🖨 Друкувати
        </button>
      </div>

      <div style={{ textAlign: 'center' }}>
        <div>{org?.full_name ?? org?.name ?? ''}</div>
        {org?.edrpou && <div style={{ fontSize: '9pt' }}>ЄДРПОУ: {org.edrpou}</div>}
      </div>

      <h2 style={{ textAlign: 'center', fontSize: '14pt', margin: '1.5rem 0 0.3rem' }}>
        ШТАТНИЙ РОЗКЛАД
      </h2>
      <p style={{ textAlign: 'center', margin: 0 }}>
        станом на {onDate ? new Date(onDate).toLocaleDateString('uk-UA') : '—'}
      </p>

      <table style={{ width: '100%', marginTop: '1.5rem', borderCollapse: 'collapse', border: '1px solid #000' }}>
        <thead>
          <tr style={{ background: '#f8fafc' }}>
            <th style={{ padding: '0.4rem', border: '1px solid #000' }}>№</th>
            {byEmp && <th style={{ padding: '0.4rem', border: '1px solid #000', textAlign: 'left' }}>Працівник</th>}
            <th style={{ padding: '0.4rem', border: '1px solid #000', textAlign: 'left' }}>Підрозділ</th>
            <th style={{ padding: '0.4rem', border: '1px solid #000', textAlign: 'left' }}>Посада</th>
            <th style={{ padding: '0.4rem', border: '1px solid #000' }}>{byEmp ? 'Ставка' : 'К-сть ставок'}</th>
            <th style={{ padding: '0.4rem', border: '1px solid #000' }}>Оклад, грн</th>
            <th style={{ padding: '0.4rem', border: '1px solid #000' }}>Фонд, грн</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={{ padding: '0.3rem', border: '1px solid #000', textAlign: 'center' }}>{i + 1}</td>
              {byEmp && <td style={{ padding: '0.3rem', border: '1px solid #000' }}>{r.pib}</td>}
              <td style={{ padding: '0.3rem', border: '1px solid #000' }}>{r.dept}</td>
              <td style={{ padding: '0.3rem', border: '1px solid #000' }}>{r.pos}</td>
              <td style={{ padding: '0.3rem', border: '1px solid #000', textAlign: 'right' }}>{r.rate}</td>
              <td style={{ padding: '0.3rem', border: '1px solid #000', textAlign: 'right' }}>{fmt2(r.salary)}</td>
              <td style={{ padding: '0.3rem', border: '1px solid #000', textAlign: 'right' }}>{fmt2(r.fund)}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={byEmp ? 7 : 6} style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center' }}>
              Немає діючих працівників на цю дату
            </td></tr>
          )}
        </tbody>
        {rows.length > 0 && (
          <tfoot>
            <tr style={{ fontWeight: 700, background: '#f8fafc' }}>
              <td colSpan={byEmp ? 4 : 3} style={{ padding: '0.4rem', border: '1px solid #000' }}>Разом</td>
              <td style={{ padding: '0.4rem', border: '1px solid #000', textAlign: 'right' }}>{totalRate.toFixed(2)}</td>
              <td style={{ padding: '0.4rem', border: '1px solid #000' }}></td>
              <td style={{ padding: '0.4rem', border: '1px solid #000', textAlign: 'right' }}>{fmt2(totalFund)}</td>
            </tr>
          </tfoot>
        )}
      </table>

      <table style={{ width: '100%', marginTop: '3rem' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%' }}>
              <div>Керівник:</div>
              <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center', width: '80%' }}>
                ___________________
              </div>
              <div style={{ fontSize: '8pt', textAlign: 'center', width: '80%' }}>(підпис, П.І.Б.)</div>
            </td>
            <td style={{ width: '50%' }}>
              <div>Головний бухгалтер:</div>
              <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center', width: '80%' }}>
                ___________________
              </div>
              <div style={{ fontSize: '8pt', textAlign: 'center', width: '80%' }}>(підпис, П.І.Б.)</div>
            </td>
          </tr>
        </tbody>
      </table>

      <style>{`
        @media print {
          .print-hide { display: none !important; }
        }
      `}</style>
    </div>
  );
}
